import http from "src/libs/http";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import MsalInstance from "./authClient";
import { getImpersonation } from "src/impersonation/impersonationStore";

export const CMS_BASE_URL = window._env_.REACT_APP_RETAILCMS_BASEURL;

export const cmsClient = http.create({
	baseURL: CMS_BASE_URL
});

const {
	interceptors: { request, response }
} = cmsClient;

request.use(
	async (config) => {
		if (typeof process !== "undefined" && process.env.NODE_ENV === "test") {
			console.error(`${config.method?.toUpperCase()}: ${config.url}`);
		}

		// Acting-as (impersonation): tag requests so the backend attributes them
		// to the impersonated user — query param on every (non-tracking) request,
		// body field on JSON writes. No-op when not impersonating; tracking
		// stays the real actor.
		const impersonating = getImpersonation();
		if (impersonating && !config.url?.includes("/tracking/")) {
			config.params = {
				...(config.params as Record<string, unknown> | undefined),
				impersonation_author: impersonating.cms_user_id
			};
			const method = (config.method ?? "get").toLowerCase();
			const body = config.data;
			if (
				(method === "post" || method === "put" || method === "patch") &&
				body &&
				typeof body === "object" &&
				!Array.isArray(body) &&
				!(body instanceof FormData) &&
				!(body instanceof Blob) &&
				(body as Record<string, unknown>).impersonation_author === undefined
			) {
				config.data = {
					...(body as Record<string, unknown>),
					impersonation_author: impersonating.cms_user_id
				};
			}
		}

		const activeAccount = MsalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
		const accounts = MsalInstance.getAllAccounts();

		if (window.authTokens.accessToken) {
			const bearer = `Bearer ${window.authTokens.accessToken}`;
			config.headers!.Authorization = bearer;
			return config;
		}

		if (!activeAccount && accounts.length === 0) {
			/*
			 * User is not signed in. Throw error or wait for user to login.
			 * Do not attempt to log a user in outside of the context of MsalProvider
			 */
			return config;
		}

		const request = {
			scopes: [window._env_.REACT_APP_AD_SCOPE],
			account: activeAccount || accounts[0]
		};

		const authResult = await MsalInstance.acquireTokenSilent(request).catch(
			async (error) => {
				if (error instanceof InteractionRequiredAuthError) {
					// fallback to interaction when silent call fails
					await MsalInstance.acquireTokenRedirect(request);
				}
			}
		);

		const bearer = `Bearer ${authResult?.accessToken}`;
		config.headers!.Authorization = bearer;

		return config;
	},
	async (error) => {
		return Promise.reject(error);
	}
);

response.use(
	async (response) => {
		return response;
	},
	async (error) => {
		return Promise.reject(error);
	}
);

export default cmsClient;
